import React from 'react';
import {
    Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
    Checkbox, FormControlLabel, Box, Alert,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import CodeBlock from './CodeBlock';
import DataTable from './DataTable';

const PARAMETER_REFERENCE = [
    { param: 'secret', required: 'Yes', description: 'Webhook authentication secret', example: 'secret=abc123...' },
    { param: 'challenge_id', required: 'No', description: 'Enroll user in a challenge', example: 'challenge_id=42' },
    { param: 'premium_duration', required: 'No', description: 'Grant premium access (1wk, 2wk, 1mo, 3mo, 6mo, 1yr, lifetime)', example: 'premium_duration=1yr' },
    { param: 'initiative_code', required: 'No', description: 'Activate a marketing initiative', example: 'initiative_code=SUMMER2025' },
    { param: 'marketing_source', required: 'No', description: 'Sets Amplitude "Account Creation Source" for new users (defaults to "ClickFunnels" if omitted). Also sent as "Marketing Source" in the initiative activation Amplitude event.', example: 'marketing_source=cf_landing_page' },
    { param: 'braze_attr[]', required: 'No', description: 'Set Braze custom attributes. Pipe-delimited name|value format. Supports multiple indexed entries.', example: 'braze_attr[0]=Campaign+Source|summer_2025' },
    { param: 'klaviyo_list_id', required: 'No', description: "Add user's Klaviyo profile to a specific list", example: 'klaviyo_list_id=XyZ123' },
];

const TROUBLESHOOTING = [
    { symptom: '401 Unauthorized', cause: 'Wrong or missing secret', fix: 'Verify CLICKFUNNELS_WEBHOOK_SECRET env var matches URL' },
    { symptom: '400 Validation Error', cause: 'Missing email/first_name in CF payload', fix: 'Check CF form has required fields mapped' },
    { symptom: '429 Too Many Requests', cause: 'Rate limit exceeded (30/min + 5,000/day per IP)', fix: 'CF retries should handle this; check for misconfigured loops' },
    { symptom: 'challenge step failed', cause: 'Invalid challenge_id', fix: 'Verify the challenge ID exists in the database' },
    { symptom: 'initiative step skipped', cause: 'User already activated this initiative', fix: 'Normal behavior — idempotent' },
    { symptom: 'User not receiving email', cause: 'Braze send failure', fix: 'Check Braze dashboard for delivery errors' },
    { symptom: 'Braze campaign not triggering', cause: 'Using "Attribute Changed" trigger', fix: 'Use "Performs Custom Event: clickfunnels_campaign_trigger" instead — see Braze Campaign Integration section' },
];

const CHECKLIST_ITEMS = [
    'Workflow created and active in CF',
    'Webhook URL has correct secret',
    'Funnel-specific params are correct (challenge_id, premium_duration, phone, klaviyo_list_id, etc.)',
    'Test submission creates user with expected steps',
    '"Set Your Password" email arrives for new users',
    'Existing users return 200 (not duplicated)',
    'If using phone: CF form collects phone, phone appears on Klaviyo profile, SMS consent is set',
    'If using klaviyo_list_id: verify the list ID exists in Klaviyo, user appears in list after submission',
];

const PARAM_COLUMNS = [
    { key: 'param', label: 'Parameter', render: (v) => <code>{v}</code> },
    { key: 'required', label: 'Required' },
    { key: 'description', label: 'Description' },
    { key: 'example', label: 'Example', render: (v) => <code style={{ fontSize: '0.75rem' }}>{v}</code> },
];

const TROUBLESHOOTING_COLUMNS = [
    { key: 'symptom', label: 'Symptom', render: (v) => <code>{v}</code> },
    { key: 'cause', label: 'Cause' },
    { key: 'fix', label: 'Fix' },
];

export default function SetupGuide() {
    return (
        <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>Setup Guide</Typography>

            {/* Step 1: Configure Workflow */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Step 1: Configure the Workflow in ClickFunnels</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <ol style={{ paddingLeft: 20, lineHeight: 2 }}>
                        <li>Log in to <strong>ClickFunnels 2.0</strong> &rarr; go to <strong>Automations</strong> &rarr; <strong>Workflows</strong></li>
                        <li>Click <strong>"+ Add Workflow"</strong> — enter a descriptive name (e.g., <code>APIv2 Profile - SS Optin Paid V2 Short</code>), select the run type, and click <strong>Create Workflow</strong></li>
                        <li><strong>Add Trigger:</strong> Select <strong>"Opt-in"</strong> — this fires when a user submits an opt-in form. Optionally scope to a specific funnel/page.</li>
                        <li><strong>Add Action &rarr; Webhook:</strong> Click the <strong>+</strong> icon, select <strong>"Webhook"</strong> (under "Other"). Paste the full webhook URL you built above. No custom headers needed — auth is via the <code>secret</code> query param.</li>
                        <li><strong>Save and Activate</strong> the workflow.</li>
                    </ol>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <strong>Alternative: Workspace-level Webhooks</strong> — Instead of a workflow, you can create a webhook in <strong>Workspace Settings &rarr; Webhooks &rarr; Add New Endpoint</strong>. These fire automatically on selected event types (e.g. <code>contact.identified</code>) without needing a workflow. The endpoint auto-detects both payload formats.
                    </Alert>
                </AccordionDetails>
            </Accordion>

            {/* Step 2: Test */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Step 2: Test the Integration</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" gutterBottom>
                        <strong>Always test on staging first.</strong> Use the staging environment toggle in the URL builder above, then use this curl command to simulate a webhook:
                    </Typography>
                    <CodeBlock>{`curl -X POST "https://staging.api.thetappingsolution.com/api/webhook/clickfunnels/create-profile?secret=<SECRET>&challenge_id=42&premium_duration=1mo" \\
  -H "Content-Type: application/json" \\
  -d '{
    "id": 12345,
    "email_address": "test@example.com",
    "first_name": "John",
    "last_name": "Doe",
    "phone_number": "+15551234567"
  }'`}</CodeBlock>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Expected response (201 for new user):</Typography>
                    <CodeBlock>{`{
  "user_id": 99999,
  "email": "test@example.com",
  "first_name": "John",
  "last_name": "Doe",
  "is_new_user": true,
  "steps": {
    "welcome_email": { "status": "success" },
    "challenge": { "status": "success", "user_challenge_id": 789, "is_existing": false },
    "premium": { "status": "success", "duration": "1mo" },
    "klaviyo_phone": { "status": "success" },
    "klaviyo_list": { "status": "success" }
  }
}`}</CodeBlock>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        <strong>End-to-End Test:</strong> Submit the actual CF landing page form with a test email, check CF Workflow execution log, then verify in the API database that the user was created with expected enrollments.
                    </Typography>
                </AccordionDetails>
            </Accordion>

            {/* Step 3: Per-Funnel Checklist */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Step 3: Per-Funnel Checklist</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 1 }}>For each funnel you set up, confirm:</Typography>
                    {CHECKLIST_ITEMS.map((item, i) => (
                        <FormControlLabel
                            key={i}
                            control={<Checkbox size="small" />}
                            label={<Typography variant="body2">{item}</Typography>}
                            sx={{ display: 'flex', ml: 0 }}
                        />
                    ))}
                </AccordionDetails>
            </Accordion>

            {/* Parameter Reference */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Parameter Reference</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <DataTable columns={PARAM_COLUMNS} rows={PARAMETER_REFERENCE} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        <strong>Phone format:</strong> US numbers accepted as 10 digits (e.g. <code>8475551234</code>) or 11 digits with leading 1 (e.g. <code>18475551234</code>). Formatting characters are stripped automatically. The phone is NOT stored in our database — it's passed through to Klaviyo only.
                    </Typography>
                </AccordionDetails>
            </Accordion>

            {/* Braze Campaign Integration */}
            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Braze Campaign Integration</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Do NOT use "Custom Attribute Value Changed" as a Braze campaign trigger.</strong> Attribute changes via the REST API do not reliably trigger action-based campaigns.
                    </Alert>
                    <Typography variant="body2" gutterBottom>
                        When Braze attributes are included in the webhook, the endpoint automatically fires a custom event called <code>clickfunnels_campaign_trigger</code> alongside setting user attributes.
                    </Typography>
                    <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography variant="body2"><strong>How to set up your Braze campaign:</strong></Typography>
                        <ol style={{ paddingLeft: 20, lineHeight: 2 }}>
                            <li>Use <strong>"Performs Custom Event"</strong> as the campaign trigger</li>
                            <li>Select event name: <code>clickfunnels_campaign_trigger</code></li>
                            <li>Optionally filter on event properties (e.g., <code>Campaign Source</code> equals <code>summer_2025</code>)</li>
                            <li>Use Liquid in email templates to access event properties: <code>{'{{event_properties.${property_name}}}'}</code></li>
                        </ol>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2"><strong>Key details:</strong></Typography>
                        <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                            <li><Typography variant="body2">All Braze attributes are sent as both <strong>user profile attributes</strong> (for segmentation) and <strong>event properties</strong> (for campaign triggering).</Typography></li>
                            <li><Typography variant="body2">Attribute values are automatically type-cast: <code>TRUE</code>/<code>FALSE</code> become booleans, numeric strings become numbers.</Typography></li>
                            <li><Typography variant="body2">Attributes and the event are sent in a single API call to Braze for consistency.</Typography></li>
                        </ul>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Troubleshooting */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Troubleshooting</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <DataTable columns={TROUBLESHOOTING_COLUMNS} rows={TROUBLESHOOTING} />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2"><strong>Key behaviors:</strong></Typography>
                        <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                            <li><Typography variant="body2"><strong>Idempotent:</strong> Submitting the same email twice returns the existing user (200) and skips the welcome email.</Typography></li>
                            <li><Typography variant="body2"><strong>Rate limiting:</strong> 30 requests/min and 5,000/day per IP. CF retries are handled gracefully.</Typography></li>
                            <li><Typography variant="body2"><strong>Email normalization:</strong> Emails are lowercased automatically (<code>John@Example.COM</code> &rarr; <code>john@example.com</code>).</Typography></li>
                        </ul>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Supported Payload Formats */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Supported Payload Formats</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        The endpoint auto-detects four payload formats. No configuration needed.
                    </Typography>

                    <Typography variant="subtitle2">1. Workflow Webhook (primary)</Typography>
                    <CodeBlock>{`{
  "data": {
    "id": "1131218249",
    "type": "contact",
    "attributes": {
      "id": 1131218249,
      "email_address": "user@example.com",
      "first_name": "John",
      "last_name": "Doe",
      "phone_number": "+18472555555",
      "workspace_id": 5934
    }
  },
  "event_id": "587c59c6-...",
  "event_type": "workflows/steps/deliver_webhook_step.executed"
}`}</CodeBlock>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>2. Flat Contact Object (legacy)</Typography>
                    <CodeBlock>{`{
  "id": 45,
  "email_address": "user@example.com",
  "first_name": "John",
  "last_name": "Doe"
}`}</CodeBlock>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>3. Event Webhook (legacy)</Typography>
                    <CodeBlock>{`{
  "data": {
    "id": 236296209,
    "email_address": "user@example.com",
    "first_name": "John",
    "last_name": "Doe"
  },
  "event_id": "123b4741-...",
  "event_type": "contact.identified"
}`}</CodeBlock>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>4. Direct JSON (for PAT-authenticated API calls)</Typography>
                    <CodeBlock>{`{
  "email": "user@example.com",
  "first_name": "John",
  "last_name": "Doe",
  "challenge_id": 42,
  "premium_duration": "1yr"
}`}</CodeBlock>
                </AccordionDetails>
            </Accordion>

            {/* Additional Scripts */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>Additional Scripts (FB Pixel, Premium Redirect)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="subtitle2" gutterBottom>Facebook Pixel with Advanced Matching</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Add this to the <strong>Page Header Code</strong> (Settings &rarr; Tracking Code) in your CF page editor. Replace <code>YOUR_PIXEL_ID</code> with your actual Facebook Pixel ID.
                    </Typography>
                    <CodeBlock>{`<script>
// ─── Configure your Pixel ID ────────────────────────────────────────
const FB_PIXEL_ID = 'YOUR_PIXEL_ID';
// ─────────────────────────────────────────────────────────────────────

function getCookie(name) {
  var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

const rawEmail     = getCookie('contact_email_address')  || localStorage.getItem('garlic::cf.thetappingsolution.com*:email');
const rawPhone     = getCookie('contact_phone_number')   || localStorage.getItem('garlic::cf.thetappingsolution.com*:phone_number');
const rawFirstName = getCookie('contact_first_name')     || localStorage.getItem('garlic::cf.thetappingsolution.com*:first_name');
const rawLastName  = getCookie('contact_last_name')  || localStorage.getItem('garlic::cf.thetappingsolution.com*:last_name');

const cleanEmail     = rawEmail     ? rawEmail.trim().toLowerCase()     : null;
const cleanPhone     = rawPhone     ? rawPhone.replace(/\\D/g, '')      : null;
const cleanFirstName = rawFirstName ? rawFirstName.trim().toLowerCase() : null;
const cleanLastName  = rawLastName  ? rawLastName.trim().toLowerCase()  : null;

const advancedMatching = {};
if (cleanEmail)     advancedMatching.em = cleanEmail;
if (cleanPhone)     advancedMatching.ph = cleanPhone;
if (cleanFirstName) advancedMatching.fn = cleanFirstName;
if (cleanLastName)  advancedMatching.ln = cleanLastName;

!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');

fbq('init', FB_PIXEL_ID, advancedMatching);
fbq('track', 'PageView');
</script>`}</CodeBlock>

                    <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>Premium Check Redirect</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Checks if a user is premium (via RevenueCat) and redirects accordingly. Add to the <strong>Page Header Code</strong> of the intermediate/routing page. Update the <code>not_premium</code> and <code>is_premium</code> URLs.
                    </Typography>
                    <CodeBlock>{`<script>
function getCookie(name) {
  var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

const rawEmail = getCookie('contact_email_address') || localStorage.getItem('garlic::cf.thetappingsolution.com*:email');
const cleanEmail = rawEmail ? rawEmail.trim().toLowerCase() : null;

// ─── Configure these URLs for your funnel ────────────────────────────
const NOT_PREMIUM_URL = "https://cf.thetappingsolution.com/ss-14d-67-paid-v2-premium";
const IS_PREMIUM_URL  = "https://cf.thetappingsolution.com/ss-premium-ty-paid";
const API_BASE = "https://api.thetappingsolution.com";
// ─────────────────────────────────────────────────────────────────────

if (!cleanEmail) {
  // No email found — treat as not premium
  window.location.href = NOT_PREMIUM_URL;
} else {
  fetch(API_BASE + '/api/webhook/clickfunnels/check-premium?email=' + encodeURIComponent(cleanEmail))
    .then(function(r) { return r.json(); })
    .then(function(data) {
      window.location.href = data.is_premium ? IS_PREMIUM_URL : NOT_PREMIUM_URL;
    })
    .catch(function() {
      // API error — default to not premium so the user isn't stuck
      window.location.href = NOT_PREMIUM_URL;
    });
}
</script>`}</CodeBlock>
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
}
